import React, { useEffect, useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/database";
import { Alert } from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import { connect } from "react-redux";
import {
  dispatchSessionExpired,
  VIEWSPARK_SESSION_EXPIRED,
} from "../../utils/sessionExpiryEvents";
import {
  isSessionExpClaimPast,
  sessionRoleDiffersFromRtdb,
} from "../../utils/adminRoleSessionWatch";
import {
  VIEWSPARK_PERMISSION_DENIED,
  recoverFromFirebasePermissionDenied,
} from "../../utils/firebasePermissionDenied";
import { isWebOtpEnabled } from "../../utils/firebase/twoFactorAuthConfig";
import * as modalActions from "../../utils/actions/modals";
import s from "./SessionAndPhoneGate.scss";

const SESSION_WARN_MS = 5 * 60 * 1000;
const SESSION_DISMISS_KEY = "vs-admin-phone-dismiss";

function SessionExpiryWatcherView() {
  const [warn, setWarn] = useState(false);

  useEffect(() => {
    const onExpired = async () => {
      try {
        await firebase.auth().signOut();
      } catch {
        /* ignore */
      }
      window.location.assign("/login?session=expired");
    };
  const onPermissionDenied = () => {
    recoverFromFirebasePermissionDenied();
  };
    window.addEventListener(VIEWSPARK_SESSION_EXPIRED, onExpired);
    window.addEventListener(VIEWSPARK_PERMISSION_DENIED, onPermissionDenied);
    return () => {
      window.removeEventListener(VIEWSPARK_SESSION_EXPIRED, onExpired);
      window.removeEventListener(VIEWSPARK_PERMISSION_DENIED, onPermissionDenied);
    };
  }, []);

  useEffect(() => {
    const tick = async () => {
      const u = firebase.auth().currentUser;
      if (!u || warn) {
        return;
      }
      try {
        const r = await u.getIdTokenResult(false);
        const exp = r.claims.session_exp;
        if (typeof exp !== "number") {
          return;
        }
        const msLeft = exp * 1000 - Date.now();
        if (msLeft > 0 && msLeft <= SESSION_WARN_MS) {
          setWarn(true);
        }
      } catch {
        /* ignore */
      }
    };
    const id = window.setInterval(() => {
      tick();
    }, 45000);
    tick();
    return () => window.clearInterval(id);
  }, [warn]);

  if (!warn) {
    return null;
  }
  return (
    <Alert
      color="warning"
      className="mb-0 rounded-0 text-center py-2"
      toggle={() => setWarn(false)}
    >
      Your admin session will expire soon. Save your work; you may need to sign in again
      (including verification code if required).
    </Alert>
  );
}

function normalizeRealmIds(claims: Record<string, unknown>): string[] {
  const raw = claims.__realms;
  let list: unknown[] | null = null;
  if (Array.isArray(raw)) {
    list = raw;
  } else if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        list = parsed;
      }
    } catch {
      list = null;
    }
  }
  if (!list) {
    return [];
  }
  return list.filter((x): x is string => typeof x === "string");
}

function resolveCharityIdFromClaims(
  claims: Record<string, unknown>,
  charityFromRedux: string
): string {
  if (charityFromRedux) {
    return charityFromRedux;
  }
  const target = claims.targetRealm;
  if (typeof target === "string" && target.length > 0) {
    return target;
  }
  const realms = normalizeRealmIds(claims);
  const first = realms.find(x => x !== "superadmin" && x !== "donation-reader");
  return typeof first === "string" ? first : "";
}

function AdminPhonePromptBarView(props: {
  currentCharity?: { id: string };
  charities?: Array<{ id: string }>;
  userId?: string;
  isLoggedIn: boolean;
  toggleUserProfileModal: () => void;
}) {
  const { currentCharity, charities, userId, isLoggedIn, toggleUserProfileModal } = props;
  const [show, setShow] = useState(false);
  const [dontRemindPermanently, setDontRemindPermanently] = useState(false);

  const charityIdsKey =
    (charities && charities.map(c => c.id).filter(Boolean).join("\0")) || "";

  useEffect(() => {
    if (!isLoggedIn) {
      setShow(false);
      return;
    }
    if (typeof firebase.database !== "function") {
      return;
    }
    if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") {
      return;
    }

    let cancelled = false;
    let detachListeners: (() => void) | undefined;

    const charityFromRedux =
      (currentCharity && currentCharity.id) ||
      (charities && charities.length > 0 ? charities[0].id : "") ||
      "";

    const runForUser = async (u: firebase.User) => {
      if (cancelled || !u) {
        return;
      }

      if (!(await isWebOtpEnabled())) {
        if (!cancelled) {
          setShow(false);
        }
        return;
      }

      let claims: Record<string, unknown> = {};
      try {
        const tr = await u.getIdTokenResult(false);
        claims = (tr.claims || {}) as Record<string, unknown>;
      } catch {
        /* redux-only resolution */
      }

      const charityId = resolveCharityIdFromClaims(claims, charityFromRedux);
      const realId = claims.realId;
      const adminId =
        (userId && String(userId).trim()) ||
        (typeof realId === "string" && realId.length > 0 ? realId : "") ||
        u.uid;

      if (!charityId || !adminId) {
        if (!cancelled) {
          setShow(false);
        }
        return;
      }

      const db = firebase.database();
      const settingsRef = `global/userSettings/${u.uid}/dontRemindPhone`;
      const phonePath = `${charityId}/users/${adminId}/phoneNumber`;

      const refresh = async () => {
        if (cancelled) {
          return;
        }
        if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") {
          setShow(false);
          return;
        }
        let dismissed = false;
        try {
          const dSnap = await db.ref(settingsRef).once("value");
          dismissed = dSnap.val() === true;
        } catch {
          dismissed = false;
        }
        if (cancelled) {
          return;
        }
        if (dismissed) {
          setShow(false);
          return;
        }
        try {
          const pSnap = await db.ref(phonePath).once("value");
          if (cancelled) {
            return;
          }
          const raw = pSnap.val();
          const digits = raw != null ? String(raw).replace(/\D/g, "") : "";
          setShow(digits.length < 10);
        } catch {
          if (!cancelled) {
            setShow(true);
          }
        }
      };

      await refresh();
      if (cancelled) {
        return;
      }

      const dontRef = db.ref(settingsRef);
      const phoneRef = db.ref(phonePath);
      const onVal = () => {
        void refresh();
      };
      dontRef.on("value", onVal);
      phoneRef.on("value", onVal);
      detachListeners = () => {
        dontRef.off("value", onVal);
        phoneRef.off("value", onVal);
      };
    };

    const unsub = firebase.auth().onAuthStateChanged(u => {
      if (detachListeners) {
        detachListeners();
        detachListeners = undefined;
      }
      if (u) {
        void runForUser(u);
      } else if (!cancelled) {
        setShow(false);
      }
    });

    return () => {
      cancelled = true;
      unsub();
      if (detachListeners) {
        detachListeners();
      }
    };
  }, [currentCharity?.id, charityIdsKey, userId, isLoggedIn]);

  if (!show) {
    return null;
  }
  const dismissForSession = () => {
    sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    setShow(false);
  };
  const onRemindMeLater = () => {
    sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    if (dontRemindPermanently) {
      const uid = firebase.auth().currentUser?.uid;
      if (uid) {
        firebase.database().ref(`global/userSettings/${uid}/dontRemindPhone`).set(true);
      }
    }
    setShow(false);
  };
  return (
    <div className={s.wrap} role="status">
      <button type="button" className={s.dismiss} onClick={dismissForSession} aria-label="Dismiss">
        ×
      </button>
      <p className={s.row1}>
        Add a phone number to receive two-factor authentication codes through SMS.{" "}
        <button type="button" className={s.profileLink} onClick={() => toggleUserProfileModal()}>
          Open your profile
        </button>{" "}
        to add it.
      </p>
      <div className={s.row2}>
        <div className={s.actions}>
          <button type="button" className={s.remindLink} onClick={onRemindMeLater}>
            Remind me later
          </button>
          <div
            className={s.checkboxLabel}
            role="checkbox"
            aria-checked={dontRemindPermanently}
            tabIndex={0}
            onClick={() => setDontRemindPermanently(v => !v)}
            onKeyDown={e => {
              if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                setDontRemindPermanently(v => !v);
              }
            }}
          >
            <span
              className={
                dontRemindPermanently ? `${s.checkboxBox} ${s.checkboxBoxChecked}` : s.checkboxBox
              }
              aria-hidden="true"
            />
            <span className={s.checkboxText}>Don&apos;t remind me anymore</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapPhoneState = state => ({
  currentCharity: state.user.currentCharity,
  charities: state.user.charities,
  userId: state.user.id,
  isLoggedIn: state.user.isLoggedIn,
});

const mapPhoneDispatch = {
  toggleUserProfileModal: modalActions.toggleUserProfileModal,
};

function AdminRoleAndSessionWatcherView(props: {
  currentCharity?: { id: string; role: string };
  charities?: Array<{ id: string }>;
  userId?: string;
  isLoggedIn: boolean;
}) {
  const { currentCharity, charities, userId, isLoggedIn } = props;
  const sessionRole = currentCharity?.role || "";

  const charityIdsKey =
    (charities && charities.map(c => c.id).filter(Boolean).join("\0")) || "";

  useEffect(() => {
    if (!isLoggedIn || typeof firebase.database !== "function") {
      return;
    }

    const checkSessionOnTabFocus = async () => {
      const u = firebase.auth().currentUser;
      if (!u) {
        return;
      }
      try {
        const tokenResult = await u.getIdTokenResult(true);
        if (
          isSessionExpClaimPast(
            (tokenResult.claims || {}) as Record<string, unknown>
          )
        ) {
          dispatchSessionExpired();
        }
      } catch {
        dispatchSessionExpired();
      }
    };

    const onWindowFocus = () => {
      void checkSessionOnTabFocus();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkSessionOnTabFocus();
      }
    };

    window.addEventListener("focus", onWindowFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("focus", onWindowFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !sessionRole || typeof firebase.database !== "function") {
      return;
    }

    let cancelled = false;
    let detachRoleListener: (() => void) | undefined;

    const charityFromRedux =
      (currentCharity && currentCharity.id) ||
      (charities && charities.length > 0 ? charities[0].id : "") ||
      "";

    const runForUser = async (u: firebase.User) => {
      if (cancelled || !u) {
        return;
      }

      let claims: Record<string, unknown> = {};
      try {
        const tr = await u.getIdTokenResult(false);
        claims = (tr.claims || {}) as Record<string, unknown>;
      } catch {
        /* redux-only resolution */
      }

      const charityId = resolveCharityIdFromClaims(claims, charityFromRedux);
      const realId = claims.realId;
      const adminId =
        (userId && String(userId).trim()) ||
        (typeof realId === "string" && realId.length > 0 ? realId : "") ||
        u.uid;

      if (!charityId || !adminId) {
        return;
      }

      const roleRef = firebase.database().ref(`${charityId}/users/${adminId}/role`);
      const onRoleChange = (snap: firebase.database.DataSnapshot) => {
        if (cancelled) {
          return;
        }
        if (sessionRoleDiffersFromRtdb(sessionRole, snap.val())) {
          dispatchSessionExpired();
        }
      };

      roleRef.on("value", onRoleChange);
      detachRoleListener = () => {
        roleRef.off("value", onRoleChange);
      };
    };

    const unsub = firebase.auth().onAuthStateChanged(u => {
      if (detachRoleListener) {
        detachRoleListener();
        detachRoleListener = undefined;
      }
      if (u) {
        void runForUser(u);
      }
    });

    return () => {
      cancelled = true;
      unsub();
      if (detachRoleListener) {
        detachRoleListener();
      }
    };
  }, [
    currentCharity?.id,
    charityIdsKey,
    userId,
    isLoggedIn,
    sessionRole,
  ]);

  return null;
}

const AdminRoleAndSessionWatcher = connect(mapPhoneState)(
  AdminRoleAndSessionWatcherView
);

const AdminPhonePromptBar = withStyles(s)(
  connect(mapPhoneState, mapPhoneDispatch)(AdminPhonePromptBarView)
);

export { AdminPhonePromptBar };

export default function SessionAndPhoneGate() {
  return (
    <>
      <SessionExpiryWatcherView />
      <AdminRoleAndSessionWatcher />
    </>
  );
}
